import React, { Fragment, useMemo, useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import {
  Card, CardContent, Checkbox, Grid, Stack, FormGroup, FormControl, MenuItem,
  CardHeader, Typography, RadioGroup, Radio,
  Divider,
  Button,
  Popper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  Menu,
  CircularProgress
} from '@mui/material';
import flightStyles from './myflights.module.css';
import styles from '../../../custom/style.module.css';
import { BorderLinearProgress, CustomButton, CustomCheckbox, CustomDatePicker, CustomFormHelperText, CustomInputLabel, CustomRadio, CustomSearch, CustomSelectRounded, InputProps, SelectInputProps, StyledCard, StyledCardTitle } from 'custom/components';
import AddAdhocFlightModal from './AddAdhocFlightModal';
import { Link } from 'react-router-dom';
import { DateFormat, DeleteIcon, EditIcon, TickIcon, contractType as contractTypeEnum } from 'custom/constants';
import moment from 'moment-timezone';
import cookies from 'utils/cookies';
import dayjs from 'dayjs';
import Countdown from 'react-countdown';
import { dateTolocale } from 'utils/converter';
import { grey } from '@mui/material/colors';
import { AttachFile as AttachFileIcon, Cancel, Done, ExpandMore, Search, WarningAmber } from '@mui/icons-material';
import airlineLogo from 'utils/airlinelogo';
import FilterIcon from '../../../assets/images/actionIcons/filterLine.svg';
import TableWrapper from '../common/TanStackTableWrapper';

const STACKFILTER = {
  direction: {
    xs: 'column',
    lg: 'row',
  },
  align: {
    xs: 'flex-start',
    lg: 'center',
  }
};

const station = cookies.get('station_code') || null;

const getpercentage = (date1, date2) => {
  let total = moment(date2).diff(date1, 'minutes');
  let current = moment(moment().utc().format('YYYY-MM-DD HH:mm:ss')).diff(date1, 'minutes');
  if (current < 0) {
    return 0;
  } else {
    if (current >= total) return 100;
    let rem = Math.round((current / total) * 100);
    return rem;
  }
};

const getCountdown = (date) => {
  let dateUTC = moment.utc(date).local().format('YYYY-MM-DDTHH:mm:ss');
  return new Date(dateUTC);
};

const renderer = ({ hours, minutes, completed }) => {
  if (completed) {
    // Render a completed state
    return '00:00';
  } else {
    // Render a countdown
    return <span>{String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}</span>;
  }
};

// renderer only prints HH:mm, so ticking once a minute is equivalent to the 1s default
const COUNTDOWN_INTERVAL = 60000;

// folder view mounts one heavy MUI card per flight, so cards are appended as the user scrolls
const CARDS_PER_PAGE = 36;

const statusColor = (statusData, type) => {
  let color = 'var(--primaryblue)';
  let className = flightStyles.blueTop;
  if (statusData) {
    if (statusData.status === 'Finalized') {
      color = 'var(--finalized)';
      className = flightStyles.greenTop;
    } else if (statusData.status === 'Submitted') { //inprogress
      color = 'var(--in_progress)';
      className = flightStyles.amberTop;
    } else if (statusData.status === 'Rejected') {
      color = 'var(--rejected)';
      className = flightStyles.redTop;
    } else if (statusData.status === 'Reopen') {
      color = 'var(--primaryblue)';
      className = flightStyles.blueTop;
    } else if (statusData.status === 'Approved' || statusData.approved_acc || statusData.approved_air) {
      color = 'var(--approved)';
      className = flightStyles.greenTop;
    }
  }
  return type === 'className' ? className : color;
};

const FlightCard = React.memo(function FlightCard({ flight, contractType, handleOpen, handleDelete, handlePopover, handlePopoverClose }) {
  const code = flight?.airline?.code;
  const logo = airlineLogo(code);
  let airlineLogoTag = <img src={logo} alt='img' height={50} />;

  if (code) {
    switch (code) {
      case 'YY':
        airlineLogoTag = (
          <div className={flightStyles.logoWrapper}>
            <img src={logo} alt='img' height={'80%'} />
          </div>
        );
        break;
      case '4Y':
        airlineLogoTag = (
          <div className={flightStyles.edLogoWrapper}>
            <img src={logo} alt='img' width={'100%'} height={'auto'} />
          </div>
        );
        break;
      default:
        airlineLogoTag = <img src={logo} alt='img' height={50} />;
        break;
    }
  } else {
    airlineLogoTag = <div className={flightStyles.noLogo}></div>;
  }

  const report = flight?.reports?.find((r) => r.type === contractType);
  const manifest = flight.org === station ? flight?.out_ffm : flight?.in_ffm;
  const data = {
    report,
    manifest,
    type: contractType,
    no_op: flight.no_op
  };

  return (
    <Grid key={flight.id} item xs={12} sm={6} md={4} lg={3}>
      <div className={`${flightStyles.folderContainer}`}>
        <div className={flightStyles.iconWrapperOuter}>
          {!flight.flight_schedule_id ? (
            <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} className={flightStyles.iconWrapper}>
              <EditIcon onClick={() => { handleOpen('adhoc_flight', 'edit', flight); }} />
              <DeleteIcon onClick={(e) => handleDelete(flight.id, e.target)} />
            </Stack>
          ) : (
            <></>
          )}
        </div>
        <Card
          className={flightStyles.folderWrapper}
          as={Link}
          to={flight.no_op ? '#' : ((cookies.get('role') === 'GHA Agent' ? '/flight_folder/flt_report' : '/flight_folder/notes') + '?id=' + flight?.id + '&org=' + flight.org + '&dest=' + flight.dest + '&ctype=' + contractType)}
        >
          <div className={flightStyles.folderNameWrapper}>
            <Button
              onMouseEnter={(e) => handlePopover(e.currentTarget, data)}
              onMouseLeave={handlePopoverClose}
              disableRipple
              disableFocusRipple
              fullWidth
              disableTouchRipple
              className={`${styles.positionRelative} ${styles.p0}`}
              sx={{ '&.MuiButtonBase-root:hover': { bgcolor: 'transparent' } }}
            >
              <div className={`${flightStyles.folderName} ${flight.no_op === true ? flightStyles.purpleTop : statusColor(report, 'className')}`}>
                {flight?.airline?.code + flight?.flight_no}/{flight?.flight_date ? moment(flight?.flight_date).format(DateFormat).toUpperCase() : ''}
              </div>
            </Button>
            {flight?.notes?.length > 0 && <span className={flightStyles.folderBadgeGreen}>{flight?.notes?.length}</span>}
          </div>
          <CardContent className={flightStyles.cardContent}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} spacing={1} className={`${flightStyles.folderNameSub}`}>
              {flight.adhoc ? <Typography component={'span'} color={'primary'} lineHeight='normal' className={flightStyles.adhocFlight}>adhoc</Typography> : <></>}
              <span>{flight.dest === station ? 'ARR' : 'DEP'}</span></Stack>
            <Stack spacing={'0.9rem'}>
              <div className={`${styles.mb2} ${styles.fSpaceBtw}`}>
                <div>Status: <span className={flightStyles.flightStatus}><b>{(flight.status)}</b></span></div>
              </div>
              <div className={styles.fSpaceBtw}>
                <span>{flight?.reg_no || '-'}</span>
                <span>{flight?.ac_type}</span>
                <span>{flight?.org + '-' + flight?.dest}</span>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <span className={flightStyles.inlineEl}>{
                    flight.dest === station ? (flight?.act_arr ? 'A' : (flight?.est_arr ? 'E' : 'S')) :
                      (flight?.act_dept ? 'A' : (flight?.est_dept ? 'E' : 'S'))
                  }</span>
                  <span>{flight.dest === station ? dateTolocale(flight?.act_arr || flight?.est_arr || flight?.schdl_arr, 'HH:mm') : dateTolocale(flight?.act_dept || flight?.est_dept || flight?.schdl_dept, 'HH:mm')}</span>
                </Stack>
              </div>
              <div className={flightStyles.progressWrapper}><BorderLinearProgress variant='determinate' value={getpercentage(flight?.act_dept || flight?.est_dept || flight?.schdl_dept, flight?.act_arr || flight?.est_arr || flight?.schdl_arr)} className={styles.w100} /> <span><Countdown date={getCountdown(flight?.act_arr || flight?.est_arr || flight?.schdl_arr)} startTime={getCountdown(flight?.act_dept || flight?.est_dept || flight?.schdl_dept)} intervalDelay={COUNTDOWN_INTERVAL} renderer={renderer} /></span></div>
              <div className={`${styles.mt0} ${styles.fSpaceBtw}`}>
                <span>{flight.dest === station ?
                  (moment(flight?.schdl_arr).isSame(flight?.act_arr || flight?.est_arr || flight?.schdl_arr) ? 'On Time' :
                    (moment(flight?.act_arr || flight?.est_arr).isAfter(flight?.schdl_arr) ? 'Delayed ' + (moment(flight?.act_arr || flight?.est_arr).diff(moment(flight?.schdl_arr), 'minutes') / 60).toFixed(2) + ' hrs' : 'Early')) :
                  (moment(flight?.schdl_dept).isSame(flight?.act_dept || flight?.est_dept || flight?.schdl_dept) ? 'On Time' :
                    (moment(flight?.act_dept || flight?.est_dept).isAfter(flight?.schdl_dept) ? 'Delayed ' + (moment(flight?.act_dept || flight?.est_dept).diff(moment(flight?.schdl_dept), 'minutes') / 60).toFixed(2) + ' hrs' : 'Early'))
                }</span>
                <span>Gate - {flight.dest === station ? flight?.arr_gate : flight?.dept_gate}</span>
                {airlineLogoTag}
              </div>
            </Stack>
          </CardContent>
        </Card>
      </div>
    </Grid>
  );
});

const MyFlights = (props) => {
  const expiringContracts = Array.isArray(props.alert) ? props.alert : [];

  const [visibleCount, setVisibleCount] = useState(CARDS_PER_PAGE);
  const sentinelRef = useRef(null);

  const flightList = props.flightList;
  const isFolderView = props.showTableView !== 'table';

  const selectedAdvancedFilters = [
    props.flightStatus.submitted && 'Show Finalized',
    props.showRejectedFlights && 'Show Rejected',
    props.flightStatus.no_op && 'Show No Ops',
    props.flightStatus.pending && 'Show Pending',
    props.flightStatus.missing_ffm && 'Missing FFM',
    props.flightStatus.manifest_uploaded && 'Manifest Uploaded',
    props.flightStatus.manual_updated_auto && 'Manually Updated',
    props.flightStatus.updated_auto && 'Manual Entries Required',
  ].filter(Boolean);

  useEffect(() => {
    setVisibleCount(CARDS_PER_PAGE);
  }, [flightList, isFolderView]);

  useEffect(() => {
    if (!isFolderView || visibleCount >= (flightList?.length || 0)) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setVisibleCount((prev) => prev + CARDS_PER_PAGE);
      }
    }, { rootMargin: '400px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isFolderView, visibleCount, flightList]);

  const columns2 = useMemo(() => [
    {
      accessorKey: 'airline',
      header: 'Airline',
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        const code = row?.airline?.code;
        const logo = airlineLogo(code);
        let airlineLogoTag;

        if (code) {
          switch (code) {
            case 'YY':
              airlineLogoTag = (
                <div className={flightStyles.logoWrapper}>
                  <img src={logo} alt='img' height='80%' />
                </div>
              );
              break;
            case '4Y':
              airlineLogoTag = (
                <div className={flightStyles.edLogoWrapper}>
                  <img src={logo} alt='img' width='100%' height='auto' />
                </div>
              );
              break;
            case 'LH':
            case 'CM':
            case 'N3':
              airlineLogoTag = (
                <div
                  className={`${flightStyles.lgLogoWrapper} ${code === 'LH' ? flightStyles.lhLogoWrapper : ''
                  }`}
                >
                  <img src={logo} alt='img' width='80px' height='auto' />
                </div>
              );
              break;
            default:
              airlineLogoTag = <img src={logo} alt='img' height={50} />;
          }
        } else {
          airlineLogoTag = <div className={flightStyles.noLogo}></div>;
        }

        return (
          <div className={`${styles.positionRelative} ${flightStyles.columnAlignCenter}`}>
            {logo && airlineLogoTag}
            {row?.notes?.length > 0 && (
              <span
                className={`${flightStyles.folderBadgeGreen} ${flightStyles.tableNoteCount}`}
              >
                {row?.notes?.length}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'flight_no',
      header: 'Flight#',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>
            {row?.airline?.code + row?.flight_no}/
            {row?.flight_date
              ? moment(row?.flight_date).format(DateFormat).toUpperCase()
              : ''}
          </div>
        );
      },
    },
    {
      accessorKey: 'dest',
      header: 'ARR/DEPT',
      cell: (info) => {
        const row = info.row.original;
        return <div>{row.dest === station ? 'ARR' : 'DEP'}</div>;
      },
    },
    {
      accessorKey: 'flight_report_status',
      header: 'Flight Report Status',
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        const report =
          row?.reports?.find(
            (r) => r.type === props.searchFields.contract_type
          ) || {};
        const manifest = row?.org === station ? row?.out_ffm : row?.in_ffm;
        const data = {
          report,
          manifest,
          type: props.searchFields.contract_type,
          no_op: row?.no_op,
        };

        return (
          <Button
            onMouseEnter={(e) => props.handlePopover(e.currentTarget, data)}
            onMouseLeave={props.handlePopoverClose}
            disableRipple
            disableFocusRipple
            fullWidth
            disableTouchRipple
            className={`${styles.positionRelative} ${styles.p0}`}
            sx={{
              marginTop: '-2px',
              '&.MuiButtonBase-root:hover': { bgcolor: 'transparent' },
            }}
          >
            <Typography
              variant='caption'
              color='#fff'
              bgcolor={
                row.no_op === true ? '#530a7b' : statusColor(report, 'color')
              }
              borderRadius={5}
              px={0.5}
              py={0.25}
              minWidth={120}
              fontWeight={700}
              lineHeight={1}
              textAlign='center'
              width='100%'
              whiteSpace='wrap'
            >
              {row?.no_op
                ? 'No operation'
                : report?.status
                  ? report?.status.toLowerCase() === 'submitted'
                    ? 'Report not submitted'
                    : report?.status
                  : 'Not Finalized'}
            </Typography>
          </Button>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>
            {row.status
              ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
              : '-'}
          </div>
        );
      },
    },
    {
      accessorKey: 'reg_no',
      header: 'REGNO',
      cell: (info) => <div>{info.getValue() || '-'}</div>,
    },
    {
      accessorKey: 'ac_type',
      header: 'AC Type',
      cell: (info) => <div>{info.getValue()}</div>,
    },
    {
      accessorKey: 'org',
      header: 'O-D',
      cell: (info) => {
        const row = info.row.original;
        return <div>{`${row?.org}-${row?.dest}`}</div>;
      },
    },
    {
      accessorKey: 'schdl_arr_dept',
      header: 'Sch Arr/Dept',
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <b>
            {row.dest === station
              ? dateTolocale(row?.schdl_arr, 'HH:mm')
              : dateTolocale(row?.schdl_dept, 'HH:mm')}
          </b>
        );
      },
    },
    {
      accessorKey: 'drivers',
      header: 'Drivers',
      cell: (info) => {
        const row = info.row.original;
        const contractsList =
          row.contracts?.[props.searchFields.contract_type]?.[0]?.contractrows;
        const contractsPending = Boolean(props.contractsLoading?.[row.id]);

        return (
          <Accordion
            className={flightStyles.rowAccordion}
            classes={{ expanded: flightStyles.accordionExpandedRoot }}
            expanded={Boolean(props.expandedRows?.[row.id])}
            onChange={(e, isExpanded) => {
              props.handleRowExpand(row.id, isExpanded);
              if (isExpanded) props.getFreport(row);
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <AccordionSummary
              className={flightStyles.rowAccordionSummary}
              expandIcon={<ExpandMore />}
              aria-controls='accordion-content'
              id='accordion-header'
              classes={{
                expanded: flightStyles.accordionExpanded,
                content: flightStyles.accordionContent,
              }}
            >
              <Typography variant='inherit' fontStyle='italic'>
                Expand to view statistics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {contractsPending ? (
                <Typography variant='inherit' fontStyle='italic' color='textSecondary'>
                  Loading...
                </Typography>
              ) : (
                <List className={flightStyles.accordionList}>
                  {contractsList?.map((driver, index) => (
                    <ListItem
                      key={index}
                      disableGutters
                      className={flightStyles.listItem}
                      secondaryAction={Number(driver?.quantity).toFixed(2)}
                    >
                      <ListItemText
                        primary={driver.description}
                        primaryTypographyProps={{ fontSize: 'inherit' }}
                        className={flightStyles.listItemText}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </AccordionDetails>
          </Accordion>
        );
      },
    },
    {
      accessorKey: 'countdown',
      header: 'Countdown',
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className={flightStyles.progressWrapper}>
            <BorderLinearProgress
              variant='determinate'
              value={getpercentage(
                row?.act_dept || row?.est_dept || row?.schdl_dept,
                row?.act_arr || row?.est_arr || row?.schdl_arr
              )}
              className={styles.w100}
            />
            <span>
              <Countdown
                date={getCountdown(
                  row?.act_arr || row?.est_arr || row?.schdl_arr
                )}
                startTime={getCountdown(
                  row?.act_dept || row?.est_dept || row?.schdl_dept
                )}
                renderer={renderer}
              />
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'ontime',
      header: 'Flight Status',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>{row.dest === station ?
            (moment(row?.schdl_arr).isSame(row?.act_arr || row?.est_arr || row?.schdl_arr) ? 'On Time' :
              (moment(row?.act_arr || row?.est_arr).isAfter(row?.schdl_arr) ? 'Delayed' : 'Early')) :
            (moment(row?.schdl_dept).isSame(row?.act_dept || row?.est_dept || row?.schdl_dept) ? 'On Time' :
              (moment(row?.act_dept || row?.est_dept).isAfter(row?.schdl_dept) ? 'Delayed' : 'Early'))
          }</div>
        );
      },
    },
    {
      accessorKey: 'gate',
      header: 'Gate',
      cell: (info) => {
        const row = info.row.original;
        return <div>{row.org === station ? row?.dept_gate : row?.arr_gate || '-'}</div>;
      },
    },
    {
      accessorKey: 'action',
      header: 'Action',
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <Stack
            direction='row'
            spacing={2}
            justifyContent='center'
            className={styles.actionIcons}
          >
            {(row.docs && row.docs.length > 0) || !row.flight_schedule_id ? (
              <>
                {row.docs && row.docs.length > 0 && (
                  <div onClick={() => props.handleAttachmentPopover('attached_docs', true, row)}>
                    <AttachFileIcon
                      fontSize='small'
                      className={`${styles.pointer}`}
                    />
                  </div>
                )}
                {!row.flight_schedule_id && (
                  <>
                    <EditIcon
                      onClick={(e) => props.handleOpen(e, 'adhoc_flight', 'edit', row)}
                    />
                    <DeleteIcon onClick={(e) => props.handleDelete(row.id, e.target)} />
                  </>
                )}
              </>
            ) : (
              <>-</>
            )}
          </Stack>
        );
      },
    },
  ], [
    props.searchFields.contract_type,
    props.contractsLoading,
    props.expandedRows,
    props.handleRowExpand,
    props.handlePopover,
    props.handlePopoverClose,
    props.handleAttachmentPopover,
    props.handleOpen,
    props.handleDelete,
    props.getFreport,
  ]);

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <div
          className={`${styles.wAuto} ${flightStyles.headerWraperTop}`}>
          <StyledCard className={`${flightStyles.filterCard}`}>
            <StyledCardTitle gutterBottom className={`${flightStyles.filterTitle}`}>Filter Flights</StyledCardTitle>
            <Grid container columnSpacing={{
              xs: 4,
              lg: 2,
              xl: '2%'
              // xl: 'clamp(2rem, 5%, 4rem)'
            }} rowSpacing={{
              xs: 2,
              lg: 0,
            }} justifyContent={{
              xs: 'flex-start',
              xl: 'center',
            }}>
              <Grid item xs={12} sm={6} md={4} lg={'auto'} display='flex' alignItems='flex-end'>
                <CustomSearch
                  id='search'
                  variant='outlined'
                  size='small'
                  type='search'
                  placeholder='Search Eg: LH456/31AUG26'
                  name='flight'
                  className={`${styles.brInput} ${styles.minWidth190} ${styles.w100}`}
                  sx={{ '& .MuiInputBase-input::placeholder': { fontSize: '0.85rem' } }}
                  onChange={props.onFilterMain}
                  value={props.searchFields.flight}
                  onKeyDown={
                    (e) => {
                      if (e.key === 'Enter') {
                        props.fetchFlights(true);
                      }
                    }
                  }
                  InputProps={{
                    endAdornment: <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        aria-label='search'
                        edge='end'
                        onClick={props.fetchFlights}
                      >
                        <Search fontSize='inherit' />
                      </IconButton>
                    </InputAdornment>,
                  }}
                // onKeyDown={
                //   (e) => {
                //     if (e.key === 'Enter') {
                //       props.filter(true);
                //     }
                //   }
                // }
                />
              </Grid>
              <Grid item xs='auto'>
                <Divider orientation='vertical' variant='middle' flexItem sx={{
                  backgroundColor: '#ffffff8f',
                  height: '100%',
                  '@media (max-width: 900px)': { display: 'none', }
                }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3.5} lg={'auto'}>
                <Stack direction={{
                  xs: 'column',
                  lg: 'row',
                }}
                align={{
                  xs: 'flex-start',
                  lg: 'center',
                }}>
                  <CustomInputLabel htmlFor='' aria-label='Enter from date' className={`${styles.ipLabel}`}>From</CustomInputLabel>
                  <CustomDatePicker
                    slotProps={{
                      textField: {
                        ...InputProps,
                        placeholder: 'From Date',
                        fullWidth: true
                      },
                    }}
                    format='DD MMM YYYY'
                    className={`${styles.brInput} ${styles.w100} ${styles.minWidth125}`}
                    onChange={(v) => props.onFilterMain(
                      {
                        target: {
                          type: 'date',
                          name: 'from',
                          value: moment(v.$d).startOf('day').format('YYYY-MM-DD HH:mm')
                        }
                      })}
                    value={dayjs(moment(props.searchFields.from).format('YYYY-MM-DD HH:mm'))}
                    disabled={props.searchActive}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={3.5} lg={'auto'}>
                <Stack {...STACKFILTER}>
                  <CustomInputLabel htmlFor='' aria-label='Enter to date' className={`${styles.ipLabel}`}>To</CustomInputLabel>
                  <CustomDatePicker
                    slotProps={{
                      textField: {
                        ...InputProps,
                        placeholder: 'To Date',
                        fullWidth: true
                      },
                    }}
                    format='DD MMM YYYY'
                    className={`${styles.brInput} ${styles.w100} ${styles.minWidth125}`}
                    onChange={(v) => props.onFilterMain(
                      {
                        target: {
                          type: 'date',
                          name: 'to',
                          value: moment(v.$d).endOf('day').format('YYYY-MM-DD HH:mm')
                        }
                      })}
                    value={dayjs(moment(props.searchFields.to).format('YYYY-MM-DD HH:mm'))}
                    disabled={props.searchActive}
                  />
                </Stack>
              </Grid>
            </Grid>
          </StyledCard>
        </div>
        {expiringContracts.length > 0 && (
          <Box className={flightStyles.expiringBanner}>
            <span className={flightStyles.expiringIconWrap}>
              <WarningAmber className={flightStyles.expiringIcon} />
            </span>
            <Typography variant='body2' className={flightStyles.expiringTitle}>
              {`${expiringContracts.length} Contracts Expiring Soon: `}
            </Typography>
            <Stack direction='row' spacing={1} className={flightStyles.expiringChips}>
              {expiringContracts.map((contract, index) => (
                <Stack direction='row' spacing={1} key={`${contract.code}-${index}`} 
                  divider={<Divider orientation='vertical' flexItem sx={{ borderColor: grey[400] }} />}
                  className={flightStyles.expiringChip}>
                  <b>{contract.code}</b>
                  <span>{contract.endDate}</span>
                </Stack>
              ))}
            </Stack>
            <Typography variant='body2' className={flightStyles.expiringNote}>
              Please contact the Admin to renew the contract(s).
            </Typography>
          </Box>
        )}
        <Grid container flexWrap={'wrap'} spacing={0}>
          <Grid item xs={12} md={12} lg={12} xl={2.5} className={flightStyles.addFlightWrapper}>
            <div className={flightStyles.addFlight}>
              <CustomButton variant='contained' onClick={() => { props.handleOpen('adhoc_flight', 'add'); }} className={styles.ml3}>Add Adhoc Flight/Truck</CustomButton>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={7} lg flexWrap={'wrap'}>
            <Stack direction='row' className={`${flightStyles.checkboxFilter}`}>
              <FormGroup aria-label='position' row className={`${styles.h100}`}>
                <Stack direction='row' justifyContent='center' spacing={3}>
                  <CustomCheckbox
                    control={<Checkbox
                      inputProps={{ 'aria-label': 'arrival', }}
                      size='small'
                      checked={props.flightStatus.arr}
                      onChange={props.changeCheck}
                      disabled={props.searchActive}
                      name='arr' />}
                    label='Arrival Flights'
                    classes={{
                      root: flightStyles.flightCheckboxRoot,
                      label: flightStyles.flightCheckboxLabel,
                    }}
                  />
                  <CustomCheckbox
                    control={<Checkbox
                      inputProps={{ 'aria-label': 'departure', }}
                      size='small'
                      checked={props.flightStatus.dept}
                      onChange={props.changeCheck} name='dept'
                      disabled={props.searchActive} />}
                    label='Departure Flights'
                    classes={{
                      root: flightStyles.flightCheckboxRoot,
                      label: flightStyles.flightCheckboxLabel,
                    }}
                  />
                </Stack>
              </FormGroup>

              <div className={`${flightStyles.advancedFilterWrapper}`}>
                <Stack direction='row' alignItems='center' spacing={1} ml={2} className={styles.h100}>
                  <IconButton
                    id='flight-filters-button'
                    aria-label='flight filters'
                    aria-controls={props.filterAnchorEl ? 'flight-filters-menu' : undefined}
                    aria-expanded={props.filterAnchorEl ? 'true' : undefined}
                    aria-haspopup='true'
                    onClick={(event) => props.handleFilterPopover(event.currentTarget)}
                    className={`${flightStyles.filterButton}`}
                    title='Advanced Filters'
                    disabled={props.searchActive}
                  >
                    <img src={FilterIcon} alt='filter' className={`${flightStyles.filterIcon} ${styles.primarySvg} ${props.searchActive ? styles.disabledImg : ''}`} width={18} />
                    {!props.searchActive && selectedAdvancedFilters.length > 0 && (
                      <span className={flightStyles.filterCount}>{selectedAdvancedFilters.length}</span>
                    )}
                  </IconButton>
                  {!props.searchActive &&
                  <Menu
                    id='flight-filters-menu'
                    anchorEl={props.filterAnchorEl}
                    open={Boolean(props.filterAnchorEl)}
                    onClose={props.handleFilterPopoverClose}
                    MenuListProps={{ 'aria-labelledby': 'flight-filters-button' }}
                  >
                    <Box sx={{ minWidth: 200, }}>
                      <Box mb={0.75}>
                        <FormGroup>
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'reset_all', }}
                              size='small'
                              checked={false}
                              onChange={() => props.resetAdvancedFilters()}
                              name='reset_all'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                            />}
                            label='Reset Filter'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                        </FormGroup>
                      </Box>
                      <Divider />
                      <Box my={0.75}>
                        <Typography
                          id='primary-flight-filters-label'
                          variant='caption'
                          component='div'
                          className={`${flightStyles.filterMenuSectionLabel} ${flightStyles.filterMenuPrimaryLabel}`}
                        >
                          Primary Filters
                        </Typography>
                        <FormGroup aria-labelledby='primary-flight-filters-label'>
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'pending', }}
                              size='small'
                              checked={props.flightStatus.pending}
                              onChange={props.changeCheck}
                              name='pending'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                              classes={{ checked: flightStyles.filterMenuChecked }}
                            />}
                            label='Show Pending'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                          {/* {(cookies.get('role') === 'Office Agent' ||
                  cookies.get('role') === 'Senior Office Agent' ||
                  cookies.get('role') === 'System Admin' ||
                  cookies.get('role') === 'Supervisor' ||
                  cookies.get('role') === 'Manager' ||
                  cookies.get('role') === 'Accounting') && */}
                          <CustomCheckbox
                            control={
                              <Checkbox
                                checked={props.flightStatus.submitted}
                                onChange={props.changeCheck}
                                name='submitted'
                                icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                                checkedIcon={<Done fontSize='small' />}
                                disableRipple
                                classes={{ checked: flightStyles.filterMenuChecked }}
                              />
                            }
                            label='Show Finalized'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'rejected', }}
                              size='small'
                              checked={props.showRejectedFlights}
                              onChange={props.handleRejectedFlights}
                              name='rejected'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                              classes={{ checked: flightStyles.filterMenuChecked }}
                            />}
                            label='Show Rejected'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'no_op', }}
                              size='small'
                              checked={props.flightStatus.no_op}
                              onChange={props.changeCheck}
                              name='no_op'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                              classes={{ checked: flightStyles.filterMenuChecked }}
                            />}
                            label='Show No Ops'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                        </FormGroup>
                      </Box>
                      <Divider />
                      <Box my={0.75}>
                        <Typography
                          id='secondary-flight-filters-label'
                          variant='caption'
                          component='div'
                          className={`${flightStyles.filterMenuSectionLabel} ${flightStyles.filterMenuSecondaryLabel}`}
                        >
                          Secondary Filters
                        </Typography>
                        <FormGroup aria-labelledby='secondary-flight-filters-label'>
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'missing_ffm', }}
                              size='small'
                              checked={props.flightStatus.missing_ffm}
                              onChange={props.changeCheck}
                              name='missing_ffm'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                              classes={{ checked: flightStyles.filterMenuChecked }}
                            />}
                            label='Missing FFM'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'manifest_uploaded', }}
                              size='small'
                              checked={props.flightStatus.manifest_uploaded}
                              onChange={props.changeCheck}
                              name='manifest_uploaded'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                              classes={{ checked: flightStyles.filterMenuChecked }}
                            />}
                            label='Manifest Uploaded'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                        </FormGroup>
                      </Box>
                      <Divider />
                      <Box my={0.75}>
                        <FormGroup>
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'manual_updated_auto', }}
                              size='small'
                              checked={props.flightStatus.manual_updated_auto}
                              onChange={props.changeCheck}
                              name='manual_updated_auto'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                              classes={{ checked: flightStyles.filterMenuChecked }}
                            />}
                            label='Manually Updated'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                          <CustomCheckbox
                            control={<Checkbox
                              inputProps={{ 'aria-label': 'updated_auto', }}
                              size='small'
                              checked={props.flightStatus.updated_auto}
                              onChange={props.changeCheck}
                              name='updated_auto'
                              icon={<Done fontSize='small' sx={{ visibility: 'hidden' }} />}
                              checkedIcon={<Done fontSize='small' />}
                              disableRipple
                              classes={{ checked: flightStyles.filterMenuChecked }}
                            />}
                            label='Manual Entries Required'
                            classes={{
                              root: `${flightStyles.flightCheckboxRoot} ${flightStyles.filterMenuCheckboxRoot}`,
                              label: flightStyles.flightCheckboxLabel,
                            }}
                          />
                        </FormGroup>
                      </Box>
                      {/* {(cookies.get('role') === 'Office Agent' || 
                            cookies.get('role') === 'Senior Office Agent' || 
                            cookies.get('role') === 'System Admin' || 
                            cookies.get('role') === 'Supervisor' || 
                            cookies.get('role') === 'Manager' ) &&
                            <CustomCheckbox
                              control={<Checkbox inputProps={{ 'aria-label': 'hide completed', }}
                                size='small'
                                checked={props.flightStatus.hideCompleted} onChange={props.changeCheck}
                                name='hideCompleted' />}
                              label='Hide Finalized Flights'
                              disabled={props.showRejectedFlights}
                              classes={{
                                root: flightStyles.flightCheckboxRoot,
                                label: flightStyles.flightCheckboxLabel,
                              }} />
                            }  */}
                    </Box>
                  </Menu>
                  }
                </Stack>
              </div>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={12} md={5} lg={'auto'} className={`${flightStyles.selectFilter}`}>
            <Stack direction='row' justifyContent={'flex-end'} alignItems={'flex-end'} spacing={2}>
              <Stack direction='row' justifyContent={{
                md: 'flex-start',
                xs: 'left'
              }} alignItems='center' flexWrap='wrap'>
                <CustomInputLabel id='airline-label' aria-label='Enter airline' className={styles.mr2}>Airline</CustomInputLabel>
                <FormControl variant='standard' fullWidth className={flightStyles.w150}>
                  <CustomSelectRounded
                    {...SelectInputProps}
                    id='airline'
                    labelId='airline-label'
                    fullWidth
                    onChange={props.changeCheck}
                    name='airline'
                    value={!props.flightStatus.airline ? 'placeholder' : props.flightStatus.airline}
                    variant='outlined'
                    className={`${styles.brInput}`}
                    classes={{ select: !props.flightStatus.airline ? styles.colorPlaceholder : '' }}
                    disabled={props.searchActive}
                  >
                    <MenuItem value='placeholder' disabled>Select an Airline</MenuItem>
                    <MenuItem value=''>All</MenuItem>
                    {props.airlines.map((airline, i) => (
                      <MenuItem key={i} value={airline.code}>
                        {airline.code}
                      </MenuItem>
                    ))}
                  </CustomSelectRounded>
                  <CustomFormHelperText>{''}</CustomFormHelperText>
                </FormControl>
              </Stack>
              <Stack direction='row' alignItems='center' justifyContent={'flex-start'} flexWrap='wrap'>
                <CustomInputLabel id='contract_type-label' aria-label='Select Department' className={styles.mr2}>Department</CustomInputLabel>
                <FormControl variant='standard' fullWidth className={flightStyles.w160}>
                  <CustomSelectRounded
                    {...SelectInputProps}
                    fullWidth
                    id='contract_type'
                    labelId='contract_type'
                    name='contract_type'
                    variant='outlined'
                    onChange={(v) => {
                      props.onFilterMain({
                        target: {
                          type: 'contract_type',
                          name: 'contract_type',
                          value: v ? v.target.value : null
                        }
                      });
                    }}
                    value={props.searchFields.contract_type}
                    className={`${styles.brInput}`}
                    classes={{ select: !props.searchFields.contract_type ? styles.colorPlaceholder : '' }}
                    disabled={props.searchActive}
                  >
                    <MenuItem value='placeholder' disabled>Select Department</MenuItem>
                    {
                      ((props.contractTypes && props.contractTypes.length > 0)
                        ? props.contractTypes
                        : (cookies.get('contract_types')?.length > 0
                          ? cookies.get('contract_types')
                          : Object.values(contractTypeEnum))
                      ).map((contractType, index) => (
                        <MenuItem key={index} value={contractType}>{contractType}</MenuItem>
                      ))
                    }
                  </CustomSelectRounded>
                </FormControl>
              </Stack>
            </Stack>

          </Grid>
        </Grid>
        <StyledCard className={flightStyles.flightFolderWrapper} sx={props.showTableView === 'folder' ? {
          overflow: 'auto',
          padding: '64px 38px'
        } : { pt: '64px' }}>
          <div className={flightStyles.statusWrapper}>
            <RadioGroup
              aria-labelledby='folder-view-radio-buttons-group'
              name='folder-view-radio-buttons-group'
              value={props.showTableView}
              row
              onChange={props.handleToggle}
              className={`${styles.radioToggleGroup} ${flightStyles.flightSwitch}`}
            >
              <CustomRadio
                value={'folder'}
                control={<Radio className={styles.radioToggleBtn} />}
                label={'Folder View'}
                className={`${styles.radioToggle} ${props.showTableView === 'folder' ? styles.radioToggleBtnChecked : ''}`}
                classes={{ label: styles.radioToggleLabel }}
              />
              <CustomRadio
                value={'table'}
                control={<Radio className={styles.radioToggleBtn} />}
                label={'Table View'}
                className={`${styles.radioToggle} ${props.showTableView === 'table' ? styles.radioToggleBtnChecked : ''}`}
                classes={{ label: styles.radioToggleLabel }}
              />
            </RadioGroup>
          </div>

          <Grid container flexWrap={'wrap'} rowSpacing={12} columnSpacing={2} justifyContent={{
            md: 'flex-start',
            sm: 'center',
          }}>
            {props.loading || props.viewSwitching ? (
              <Grid item xs={12} width={'100%'}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '220px',
                  width: '100%' 
                }}>
                  <CircularProgress />
                </Box>
              </Grid>
            ) : !isFolderView ? (
              <Grid item xs={24} width={'100%'}>
                <Box className={`${styles.TStableWrapper} ${flightStyles.flightTableOuter}`}>
                  <TableWrapper
                    columns={columns2}
                    data={flightList}
                    showPagination={true}
                    onRowClick={props.rowSelected}
                  />
                </Box>
              </Grid>
            ) : (
              <>
                {flightList.slice(0, visibleCount).map((flight) => (
                  <FlightCard
                    key={flight.id}
                    flight={flight}
                    contractType={props.searchFields.contract_type}
                    handleOpen={props.handleOpen}
                    handleDelete={props.handleDelete}
                    handlePopover={props.handlePopover}
                    handlePopoverClose={props.handlePopoverClose}
                  />
                ))}
                {visibleCount < flightList.length && (
                  <Grid item xs={12} width={'100%'} ref={sentinelRef}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '80px',
                      width: '100%'
                    }}>
                      <CircularProgress size={28} />
                    </Box>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </StyledCard>

        <AddAdhocFlightModal
          modal={props.modal}
          type={props.action}
          handleOpen={props.handleOpen}
          onClose={props.onClose}
          airlines={props.airlines}
          onChange={props.onChange}
          changeDateTime={props.changeDateTime}
          flightFields={props.flightFields}
          flightErrors={props.flightErrors}
          addFlight={props.addFlight}
          editFlight={props.editFlight}
          locationList={props.locationList}
          scheduleOrgDest={props.scheduleOrgDest}
        />

        <Popper
          id={'id'}
          open={Boolean(props.anchorEl)}
          anchorEl={props.anchorEl}
          placement='right-start'
        >
          <Card className={flightStyles.statusPopover}>
            <CardHeader
              className={flightStyles.statusPopoverHeader}
              title={<>
                <Typography variant='h6' color={grey[600]} fontSize='1rem' lineHeight='normal'>
                  {props.reportProps?.type}
                </Typography>
                <Typography variant='caption' color={props.reportProps?.no_op ? 'error.main' : statusColor(props.reportProps?.report, 'color')} fontWeight={700} lineHeight='normal' marginTop={'4px'} display='block'>
                  {props.reportProps?.no_op ? 'Marked as non operational flight' :
                    props.reportProps?.report?.status ? (props.reportProps?.report?.status.toLowerCase() === 'submitted' ? 'Report not submitted' : props.reportProps?.report?.status) : 'Not Finalized'}
                </Typography>
              </>
              }>
            </CardHeader>
            <Divider />
            <CardContent className={styles.p3}>
              {!props.reportProps?.report && (<p className={flightStyles.statusDesc}>
                <span className={flightStyles.statusIndicator}>Report Not Finalized</span>
              </p>)}
              {props.reportProps?.report && (<>
                <p className={flightStyles.statusDesc}>
                  <span>Accounting</span>
                  <span className={flightStyles.statusIndicator}>{props.reportProps?.report.rejected_acc ? 'Correction Requested' : props.reportProps.report.approved_acc ? 'Reviewed' : 'Pending'}</span>
                </p>
                <p className={flightStyles.statusDesc}>
                  <span>Airline Rep</span>
                  <span className={flightStyles.statusIndicator}>{props.reportProps?.report.rejected_air ? 'Correction Requested' : props.reportProps.report.approved_air ? 'Reviewed' : 'Pending'}
                  </span>
                </p>
              </>)}

              <Divider className={styles.mb2} />
              <p className={flightStyles.statusDesc}>
                <Typography variant='caption' component='span' color={grey[900]} lineHeight='normal'>
                  Manifest Upload
                </Typography>
                {props.reportProps?.manifest ? <TickIcon className={flightStyles.uploaded} /> : <Cancel color='error' sx={{ fontSize: 20 }} />}
              </p>

              {props.reportProps?.report?.status?.toLowerCase() === 'rejected' && props.reportProps?.report?.log[props.reportProps?.report?.log?.length - 1]?.remark.length > 0 &&
                <>
                  <Divider className={styles.mb2} />
                  <Typography variant='caption' component='small' color={grey[900]} lineHeight='normal' fontWeight='bold'>
                    Reason: &nbsp;
                  </Typography>
                  <Typography variant='caption' component='small' color={grey[800]} lineHeight='normal'>
                    {`${props.reportProps?.report?.log[props.reportProps?.report?.log?.length - 1]?.remark}`}
                  </Typography>
                </>}
            </CardContent>
          </Card>
        </Popper>
      </Box>
    </>
  );
};

export default MyFlights;
